import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Building2, ChevronDown, ChevronRight, Home, Truck, Eye } from 'lucide-react'
import { usePageActionContext } from '../contexts/PageActionContext'
import { useAuth } from '../contexts/AuthContext'

import ScopeSwitcher from './ScopeSwitcher'

export default function Header({ breadcrumbs = [], title, description, showDescriptionBelow, fullWidth = false }) {
  const { actions } = usePageActionContext()
  const { user, activePortal, switchPortal, isViewOnly } = useAuth()
  const navigate = useNavigate()
  const [isPortalMenuOpen, setIsPortalMenuOpen] = useState(false)
  const portalMenuRef = useRef(null)

  const roles = Array.isArray(user?.role) ? user.role : [user?.role].filter(Boolean)
  const isSuperAdmin = Boolean(
    user?.type === 'SuperAdmin' || 
    user?.email === 'superadmin@sanglob.local' || 
    roles.includes('SuperAdmin') || 
    (Array.isArray(user?.roles) && user.roles.some(r => (r.name || r) === 'SuperAdmin'))
  )
  const isAdmin = roles.includes('Admin') || isSuperAdmin
  const canSwitch = !isSuperAdmin && (user?.portal_access === 'Both' || isAdmin)
  const defaultHomeUrl = isSuperAdmin ? '/superadmin/control-tower' : (activePortal === 'vendor' ? '/vendor/dashboard' : '/dashboard')

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!portalMenuRef.current) return
      if (!portalMenuRef.current.contains(e.target)) setIsPortalMenuOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [])

  // Special handling for Dashboard & Control Tower
  const isDashboard = title === 'Executive Dashboard' || title === 'Platform Control Tower' || (breadcrumbs.length === 1 && (breadcrumbs[0].to === '/dashboard' || breadcrumbs[0].to === '/superadmin/control-tower' || breadcrumbs[0].to === '/vendor/dashboard'))

  return (
    <header
      className={`bg-white border-b border-gray-200 ${
        isDashboard
          ? 'px-4 py-1 sm:px-5 lg:px-6 min-h-[2.5rem]'
          : fullWidth
            ? 'px-2 py-2 sm:px-4 lg:px-6 min-h-[3rem]'
            : 'px-4 py-2 sm:px-6 lg:px-8 min-h-[3rem]'
      } flex items-center shrink-0 z-40 relative shadow-sm`}
    >
      <div className="flex items-center justify-between w-full">
        {/* Breadcrumbs or Dashboard Header */}
        <div className="flex flex-col">
          {isDashboard ? (
            <div className="flex items-center gap-2.5">
               <Link
                  to={defaultHomeUrl}
                  className="text-gray-400 hover:text-gray-500 transition-colors"
                  title="Home"
                >
                  <Home className="flex-shrink-0 w-4 h-4" aria-hidden="true" />
               </Link>
               <div className="flex items-baseline gap-2.5">
                 <h1 className="text-base font-semibold text-gray-900 leading-none">{title}</h1>
                 {description && !showDescriptionBelow && (
                   <span className="text-[11px] text-gray-500 font-medium hidden sm:inline-block leading-none">
                     {description}
                   </span>
                 )}
               </div>
            </div>
          ) : (
            <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-1 md:space-x-2">
              {breadcrumbs.map((crumb, index) => {
                const isLast = index === breadcrumbs.length - 1
                const isFirst = index === 0

                return (
                  <li key={crumb.to + index} className="flex items-center">
                    {!isFirst && (
                      <ChevronRight className="flex-shrink-0 w-4 h-4 text-gray-400 mx-1" aria-hidden="true" />
                    )}
                    {isFirst ? (
                      <Link
                        to={crumb.to === '/dashboard' ? defaultHomeUrl : crumb.to}
                        className="text-gray-400 hover:text-gray-500 transition-colors"
                        title="Home"
                      >
                        <Home className="flex-shrink-0 w-4 h-4" aria-hidden="true" />
                        <span className="sr-only">Home</span>
                      </Link>
                    ) : (
                      <div className="flex items-center">
                        {isLast ? (
                          <span className="text-xs font-semibold text-gray-800 uppercase tracking-wide">
                            {crumb.label}
                          </span>
                        ) : (
                          <Link
                            to={crumb.to}
                            className={`text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors ${crumb.to === '#' ? 'pointer-events-none' : ''}`}
                          >
                            {crumb.label}
                          </Link>
                        )}
                      </div>
                    )}
                  </li>
                )
              })}
            </ol>
            </nav>
          )}
          {showDescriptionBelow && description && (
            <p className="text-xs text-gray-500 italic mt-1 leading-none">
              {description}
            </p>
          )}
        </div>

        {/* Page Actions */}
        <div className="flex items-center gap-3">
          <ScopeSwitcher />
          {!isSuperAdmin && (
            <div className="relative" ref={portalMenuRef}>
              {canSwitch ? (
                <>
                  <button
                    type="button"
                    onClick={() => setIsPortalMenuOpen((v) => !v)}
                    className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                    aria-label={activePortal === 'client' ? 'Client workspace' : 'Vendor portal'}
                    title={activePortal === 'client' ? 'Client workspace' : 'Vendor portal'}
                  >
                    {activePortal === 'client' ? (
                      <Building2 className="w-4 h-4 text-indigo-600" />
                    ) : (
                      <Truck className="w-4 h-4 text-emerald-600" />
                    )}
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </button>

                  {isPortalMenuOpen && (
                    <div className="absolute right-0 mt-2 w-44 bg-white rounded-md shadow-lg py-1 z-50 ring-1 ring-black ring-opacity-5">
                      <button
                        type="button"
                        onClick={() => {
                          switchPortal('client')
                          setIsPortalMenuOpen(false)
                          navigate('/dashboard')
                        }}
                        className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 w-full text-left"
                      >
                        <Building2 className="w-4 h-4 text-indigo-600" />
                        Client
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          switchPortal('vendor')
                          setIsPortalMenuOpen(false)
                          navigate('/vendor/dashboard')
                        }}
                        className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-100 w-full text-left"
                      >
                        <Truck className="w-4 h-4 text-emerald-600" />
                        Vendor
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div
                  className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-gray-200 bg-gray-50 text-gray-500"
                  aria-label={activePortal === 'client' ? 'Client workspace' : 'Vendor portal'}
                  title={activePortal === 'client' ? 'Client workspace' : 'Vendor portal'}
                >
                  {activePortal === 'client' ? (
                    <Building2 className="w-4 h-4 text-indigo-600" />
                  ) : (
                    <Truck className="w-4 h-4 text-emerald-600" />
                  )}
                </div>
              )}
            </div>
          )}
          {isViewOnly && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200 shadow-2xs">
              <Eye className="w-3.5 h-3.5 text-slate-500" />
              Viewer (Read-Only Mode)
            </span>
          )}
          {!isViewOnly && actions}
        </div>
      </div>
    </header>
  )
}
